import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Chevron } from '../../components/atoms/Chevron'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  AttributeRef,
  CategoryDto,
  CreateCategoryInput,
  UpdateCategoryInput
} from '../../../viewmodels/useCategories'

const NAME_MAX = 128
const DESC_MAX = 1024

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: CategoryDto | null
  readonly attributes: readonly AttributeRef[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateCategoryInput | UpdateCategoryInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  name: string
  description: string
  attribute_ids: Set<string>
}

const EMPTY: FormState = {
  name: '',
  description: '',
  attribute_ids: new Set()
}

function clientValidate(state: FormState): { field: keyof FormState; message: string } | null {
  const name = state.name.trim()
  if (name.length < 1 || name.length > NAME_MAX) {
    return { field: 'name', message: `name must be 1..${NAME_MAX} chars` }
  }
  if (state.description.length > DESC_MAX) {
    return { field: 'description', message: `description must be ≤${DESC_MAX} chars` }
  }
  return null
}

/**
 * CategoryFormModal — Add + Edit shared form.
 *
 * Mirrors UserTypeFormModal's structure with the GroupsPicker swapped
 * for an AttributePicker (same dropdown-with-search shape, labelled
 * "<attribute name> (<datatype_name>)"). Client validation runs first;
 * server-side errors map back onto fields via `error.field`.
 */
export function CategoryFormModal({
  open,
  mode,
  initial,
  attributes,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  // Hoisted picker open state. The modal guards its onClose against the
  // picker so a backdrop click with the dropdown open only collapses
  // the dropdown, not the modal.
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? { ...EMPTY, attribute_ids: new Set() }
          : {
              name: initial.name,
              description: initial.description ?? '',
              attribute_ids: new Set(initial.attribute_ids)
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
      setPickerOpen(false)
    }
  }, [open, initial])

  /**
   * Guarded close: if the AttributePicker dropdown is open, a click on
   * the modal backdrop should collapse the dropdown only and leave the
   * modal up. Once the dropdown is closed, the next backdrop click
   * actually dismisses the modal.
   *
   * The closure intentionally reads from `pickerOpen` state (NOT a ref):
   * during a mousedown→click sequence on the backdrop, the picker's
   * mousedown listener doesn't fire (the panel.contains check excludes
   * backdrop targets), so pickerOpen is still true when handleClose
   * runs in the click phase. For Escape, both keydown listeners fire
   * synchronously and React batches their setState calls — the closure
   * still sees the pre-batch value (true) so the modal stays put.
   */
  const handleClose = (): void => {
    if (pickerOpen) {
      setPickerOpen(false)
      return
    }
    onClose()
  }

  const sortedAttributes = useMemo(
    () => [...attributes].sort((a, b) => a.name.localeCompare(b.name)),
    [attributes]
  )

  /**
   * Structural lock — once any active product belongs to this
   * category, the attribute_ids set is immutable. Rename + description
   * stay editable. Locked only in edit mode (fresh creates always
   * start unlocked). Per Kiran 2026-05-18.
   */
  const structuralLock = mode === 'edit' && (initial?.linked_product_count ?? 0) > 0
  const linkedCount = initial?.linked_product_count ?? 0
  const lockTooltip = structuralLock
    ? `Locked: ${linkedCount} active product${linkedCount === 1 ? '' : 's'} belong${linkedCount === 1 ? 's' : ''} to this category. Rename + description are still allowed.`
    : ''

  const toggleAttribute = (id: string): void => {
    setState((s) => {
      const next = new Set(s.attribute_ids)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { ...s, attribute_ids: next }
    })
  }

  const toggleAllAttributes = (ids: ReadonlyArray<string>, shouldSelect: boolean): void => {
    setState((s) => {
      const next = new Set(s.attribute_ids)
      if (shouldSelect) for (const id of ids) next.add(id)
      else for (const id of ids) next.delete(id)
      return { ...s, attribute_ids: next }
    })
  }

  const handleSubmit = async (): Promise<void> => {
    // If the dropdown is still open when the user clicks Add, collapse
    // it first so it doesn't visually swallow the loading state.
    setPickerOpen(false)
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      setFieldError({ field: clientErr.field, message: clientErr.message })
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const attribute_ids = Array.from(state.attribute_ids).sort()
    const payload: CreateCategoryInput | UpdateCategoryInput =
      mode === 'create'
        ? {
            name: state.name.trim(),
            description: state.description === '' ? null : state.description,
            attribute_ids
          }
        : {
            name: state.name.trim(),
            description: state.description === '' ? null : state.description,
            attribute_ids
          }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f === 'attribute_ids') {
      setFormError(result.message)
    } else if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={mode === 'create' ? 'Add category' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
        {structuralLock ? (
          <Alert tone="info">
            {lockTooltip}
          </Alert>
        ) : null}

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Bamboo Rocker"
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={state.description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={DESC_MAX}
          />
        </Field>

        <AttributePicker
          attributes={sortedAttributes}
          selected={state.attribute_ids}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onToggle={toggleAttribute}
          onToggleAll={toggleAllAttributes}
          locked={structuralLock}
          lockTooltip={lockTooltip}
        />
      </div>
    </Modal>
  )
}

/**
 * AttributePicker — dropdown with search + checkboxes. Mirrors the
 * GroupsPicker shape from UserTypeFormModal, labelled
 * "<attribute name> (<datatype_name>)" so operators see the value-shape
 * inline while picking.
 */
function AttributePicker({
  attributes,
  selected,
  open,
  onOpenChange,
  onToggle,
  onToggleAll,
  locked,
  lockTooltip
}: {
  readonly attributes: ReadonlyArray<AttributeRef>
  readonly selected: ReadonlySet<string>
  readonly open: boolean
  readonly onOpenChange: (next: boolean) => void
  readonly onToggle: (id: string) => void
  readonly onToggleAll: (ids: ReadonlyArray<string>, shouldSelect: boolean) => void
  readonly locked: boolean
  readonly lockTooltip: string
}): JSX.Element {
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    // Listen on `click`, NOT `mousedown`. With mousedown, closing the
    // dropdown synchronously between mousedown and click causes the
    // modal panel to shrink (the dropdown was contributing height), the
    // browser then re-computes the click target on the new layout, and
    // the user's intended click on Add at the bottom lands on the now-
    // exposed backdrop and dismisses the modal.
    //
    // With `click`, the target was already locked in while the dropdown
    // was still rendered, so the modal stays put and the intended
    // element (Add, Cancel, another input) receives the click before
    // the picker collapses.
    //
    // We still skip the close when the click is on the modal backdrop
    // (target outside the modal panel) — that case is owned by the
    // parent's guarded onClose, which dismisses the dropdown without
    // closing the modal.
    const onDoc = (e: MouseEvent): void => {
      const wrap = wrapRef.current
      if (wrap === null) return
      const target = e.target as Node
      if (wrap.contains(target)) return
      const dialog = wrap.closest('[role="dialog"]')
      const panel = dialog?.firstElementChild ?? null
      if (panel !== null && panel.contains(target)) {
        onOpenChange(false)
      }
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onOpenChange])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return attributes
    return attributes.filter(
      (a) => a.name.toLowerCase().includes(q) || a.datatype_name.toLowerCase().includes(q)
    )
  }, [attributes, query])

  return (
    <div className="space-y-2 border-t border-border pt-4 mt-2" ref={wrapRef}>
      <div className="flex items-baseline justify-between">
        <span className="block text-body-sm font-body text-text-primary">Attributes</span>
        <span className="text-body-xs text-text-secondary">{selected.size} selected</span>
      </div>

      <button
        type="button"
        onClick={() => { if (!locked) onOpenChange(!open) }}
        aria-expanded={open}
        disabled={locked}
        title={locked ? lockTooltip : ''}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-md border border-border bg-white text-left font-body text-body-sm text-text-primary transition ${locked ? 'cursor-not-allowed opacity-60' : 'hover:border-forest/50'}`}
      >
        <span>
          {selected.size === 0
            ? locked ? 'Locked — category is in use' : 'Select attributes…'
            : `${selected.size} attribute${selected.size === 1 ? '' : 's'} selected`}
        </span>
        <Chevron open={open} />
      </button>

      {open ? (
        <div className="rounded-md border border-border bg-bg-primary shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or datatype…"
            />
          </div>
          {filtered.length > 0 ? (() => {
            const filteredIds = filtered.map((a) => a.id)
            const selectedInFiltered = filteredIds.filter((id) => selected.has(id)).length
            const allSelected = selectedInFiltered === filteredIds.length
            const someSelected = selectedInFiltered > 0 && !allSelected
            const label =
              query.trim() === ''
                ? allSelected ? 'Deselect all' : 'Select all'
                : allSelected
                  ? `Deselect all matching (${filteredIds.length})`
                  : `Select all matching (${filteredIds.length})`
            return (
              <label className="flex items-center gap-2 px-3 py-2 bg-parchment/40 border-b border-border cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => onToggleAll(filteredIds, !allSelected)}
                />
                <span className="font-body text-body-sm text-text-primary">{label}</span>
              </label>
            )
          })() : null}
          <div className="overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 font-body text-body-sm text-text-secondary">
                No attributes match &ldquo;{query}&rdquo;.
              </p>
            ) : (
              filtered.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-parchment/40 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(a.id)}
                    onChange={() => onToggle(a.id)}
                  />
                  <span className="font-body text-body-sm text-text-primary">
                    {a.name}
                    <span className="ml-2 text-text-secondary font-mono text-body-xs">
                      ({a.datatype_name})
                    </span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}


